<template>
    <div
        class="slider lock-page-swipe"
        ref="sliderRef"
        :style="sliderStyle"
        @scroll="onScroll"
        @touchend="onScrollEnd"
        @mouseup="onScrollEnd"
        v-touch:swipe.left="onSwipeLeft"
        v-touch:swipe.right="onSwipeRight"
    >
        <div
            v-for="(col, colIndex) in visibleCols"
            :key="colKey(col)"
            class="slider-col"
            :style="{
                scrollSnapAlign: props.colAlign,
                width: props.itemWidth || '100%',
                gap: props.rowGap
            }"
            @click="setActiveCol(computeRealIndex(colIndex))"
        >
            <div class="slider-item"
                v-for="item in col"
                :key="item.id || item.code"
                :style="{
                    height: props.itemHeight || '100%'
                }"
            >
                <slot :item="item" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onActivated } from 'vue';
import stores from '@/stores';

const props = defineProps({
    items: { type: Array, default: () => [] },
    itemsInCol: { type: Number, default: 1 },
    colPreloadCount: { type: [Number, null], default: null },
    itemWidth: { type: [String, null], default: null },
    itemHeight: { type: [String, null], default: null },
    colAlign: { type: String, default: 'center' },
    colGap: { type: String, default: '0px' },
    rowGap: { type: String, default: '0px' },
    overflow: { type: [String, null], default: null },
    disabled: { type: Boolean, default: false },
    loop: { type: Boolean, default: false },
    autoScroll: { type: Boolean, default: false },
    snapType: { type: String, default: 'x mandatory' },
    storyItem: { type: Boolean, default: false },
    swipeMode: { type: Boolean, default: false }
});

const emit = defineEmits(['change']);

const sliderRef = ref(null);
const activeColIndex = ref(0);

const locale = stores.locale();

let storyItemCode;




// Разбиваем items на колонки по itemsInCol
const cols = computed(() => {
    const result = [];
    for (let i = 0; i < props.items.length; i += props.itemsInCol) {
        result.push(props.items.slice(i, i + props.itemsInCol));
    }
    return result;
});

const totalCols = computed(() => cols.value.length);

// Нормализуем индекс activeCol по модулю количества колонок
const normalizedActiveColIndex = computed(() => {
    return ((activeColIndex.value % totalCols.value) + totalCols.value) % totalCols.value;
});

// В цикличном режиме считаем видимые колонки с учетом preload
const visibleCols = computed(() => {
    if (totalCols.value === 0) {
        return [];
    }

    const preload = props.colPreloadCount ?? 0;

    if (!props.loop || props.colPreloadCount === null) {
        return cols.value.slice(startColIndex.value, endColIndex.value);
    }

    const result = [];
    const center = normalizedActiveColIndex.value;

    for (let i = center - preload; i <= center + preload; i++) {
        const idx = ((i % totalCols.value) + totalCols.value) % totalCols.value;
        result.push(cols.value[idx]);
    }
    return result;
});

// В обычном режиме start и end для среза visibleCols
const startColIndex = computed(() => {
    if (props.colPreloadCount === null) {
        return 0;
    }

    const preload = props.colPreloadCount;
    return Math.max(0, activeColIndex.value - preload);
});

const endColIndex = computed(() => {
    if (props.colPreloadCount === null) {
        return totalCols.value;
    }

    const preload = props.colPreloadCount;
    return Math.min(totalCols.value, activeColIndex.value + preload + 1);
});

const sliderStyle = computed(() => ({
    overflowX: props.swipeMode
        ? 'hidden'
        : (props.overflow === 'hidden' ? 'hidden' : 'auto'),
    scrollSnapType: props.swipeMode ? 'none' : props.snapType,
    gap: props.colGap,
    direction: locale.rtl ? 'rtl' : 'ltr',
    scrollBehavior: 'smooth', //'auto' //'smooth'
}));

// Генерируем уникальный ключ для колонки — учитываем индекс для отличия одинаковых массивов при цикле
const colKey = (col) => {
    // Можно для безопасности добавить индекс в ключ, т.к. при цикле col может повторяться
    return col.map(item => item.id || item.code).join('_');
};

// Вычисляем реальный индекс колонки в полном массиве cols по индексу в visibleCols
const computeRealIndex = (visibleIndex) => {
    if (!props.loop || props.colPreloadCount === null) {
        return startColIndex.value + visibleIndex;
    }

    const preload = props.colPreloadCount;
    const realIndex = activeColIndex.value - preload + visibleIndex;
    // Нормализуем по модулю
    return ((realIndex % totalCols.value) + totalCols.value) % totalCols.value;
};

const scrollToActiveCol = async (animate = false) => {
    await nextTick();

    const slider = sliderRef.value;

    if (!slider) {
        return;
    }

    const originalBehavior = slider.style.scrollBehavior;
    slider.style.scrollBehavior = animate ? 'smooth' : 'auto';

    // получаем элемент активной колонки
    let col;
    if (!props.loop || props.colPreloadCount === null) {
        const relativeIndex = activeColIndex.value - startColIndex.value;
        col = slider.children[relativeIndex];
    } else {
        const preload = props.colPreloadCount;
        col = slider.children[preload];
    }

    if (!col) {
        slider.style.scrollBehavior = originalBehavior;
        return;
    }

    // вместо scrollIntoView используем точный scrollLeft
    const targetLeft =
        col.offsetLeft - slider.clientWidth / 2 + col.clientWidth / 2;

    slider.scrollLeft = targetLeft;

    // возвращаем scroll-behavior
    requestAnimationFrame(() => {
        slider.style.scrollBehavior = originalBehavior || 'auto';
    });
};

const setActiveCol = async (index) => {
    if (index === activeColIndex.value) {
        return;
    }


    if (totalCols.value === 0) {
        return;
    }

    if (!props.loop && (index < 0 || index >= totalCols.value)) {
        return;
    }

    // Очистим отложенный таймер скролла — клик пользователя важнее
    clearTimeout(scrollEndTimeout);
    if (activeColIndex.value !== index) {
        activeColIndex.value = index;
        emit('change', cols.value[normalizedActiveColIndex.value]);
    }

    await scrollToActiveCol();
};

let scrollEndTimeout = null;

const onScroll = () => {
    if (props.swipeMode) {
        return;
    }

    clearTimeout(scrollEndTimeout);

    if (props.colAlign === 'none') {
        return;
    }

    if (props.loop || props.autoScroll) {
        scrollEndTimeout = setTimeout(() => {
            updateActiveColByScroll();
        }, 100);
    }
};

const onScrollEnd = () => {
    // console.log('end')
    // clearTimeout(scrollEndTimeout);
    // scrollEndTimeout = setTimeout(() => {
    //     console.log('>')
    //     updateActiveColByScroll();
    // }, 600);
};

const onSwipeLeft = () => {
    if (!props.swipeMode || props.disabled) {
        return;
    }

    goNext();
};

const onSwipeRight = () => {
    if (!props.swipeMode || props.disabled) {
        return;
    }

    goPrev();
};

const goNext = () => {
    if (props.loop || activeColIndex.value < totalCols.value - 1) {
        setActiveCol(activeColIndex.value + 1);
    }
};

const goPrev = () => {
    if (props.loop || activeColIndex.value > 0) {
        setActiveCol(activeColIndex.value - 1);
    }
};

const updateActiveColByScroll = () => {
    const slider = sliderRef.value;
    if (!slider) {
        return;
    }

    const sliderRect = slider.getBoundingClientRect();

    let minDistance = Infinity;
    let closestIndex = 0;

    Array.from(slider.children).forEach((colEl, i) => {
        const colRect = colEl.getBoundingClientRect();
        const colCenter = colRect.left + colRect.width / 2;
        const sliderCenter = sliderRect.left + sliderRect.width / 2;
        const distance = Math.abs(colCenter - sliderCenter);

        if (distance < minDistance) {
            minDistance = distance;
            closestIndex = i;
        }
    });

    let newActiveCol;
    if (!props.loop || props.colPreloadCount === null) {
        newActiveCol = startColIndex.value + closestIndex;
    } else {
        const preload = props.colPreloadCount;
        newActiveCol = activeColIndex.value - preload + closestIndex;
    }

    // В режиме без цикла проверяем границы
    if (!props.loop) {
        if (newActiveCol < 0) newActiveCol = 0;
        if (newActiveCol >= totalCols.value) newActiveCol = totalCols.value - 1;
    }

    if (newActiveCol !== activeColIndex.value) {
        setActiveCol(newActiveCol);
    }
};

const showItem = (code) => {
    if (props.storyItem) {
        storyItemCode = code;
    }

    const colIndex = cols.value.findIndex(col => col.some(item => item.id === code || item.code === code));
    if (colIndex !== -1) {
        setActiveCol(colIndex);
    }
};

watch(
    [() => props.items, () => props.itemsInCol],
    async () => {
        await nextTick(); // Убедимся, что cols пересчитались

        if (totalCols.value === 0) {
            activeColIndex.value = 0;
            return;
        }

        // Если активной колонки больше нет — сбрасываем индекс
        if (activeColIndex.value >= totalCols.value) {
            activeColIndex.value = 0;
        }

        if (props.storyItem && storyItemCode) {
            const colIndex = cols.value.findIndex(col => col.some(item => item.id === storyItemCode || item.code === storyItemCode));
            activeColIndex.value = colIndex;
        }

        await scrollToActiveCol();
    },
    { deep: true }
);

defineExpose({
    showItem
});

onActivated(() => {
    scrollToActiveCol();
});

onMounted(() => {
    scrollToActiveCol();
});
</script>

<style scoped lang="scss">
    .slider {
        width: 100%;
        display: flex;
        flex-direction: row;
        overflow-x: auto;
        scroll-behavior: auto; //smooth;
        -webkit-overflow-scrolling: touch;
        -ms-overflow-style: none;
        scrollbar-width: none;

        &::-webkit-scrollbar {
            display: none;
        }
    }

    .slider-col {
        display: flex;
        flex: 0 0 auto;
        flex-direction: column;
        align-items: center;
        scroll-snap-stop: always;

        .slider-item {
            width: 100%;
        }
    }
</style>
